import React, { useState, useEffect, useCallback, memo } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import CSSTransition from 'react-transition-group/CSSTransition';

import keyboard from 'bloko/common/constants/keyboard';
import modalHelper from 'bloko/common/modalHelper';

import ModalHeader from 'bloko/blocks/modal/ModalHeader';
import ModalFooter from 'bloko/blocks/modal/ModalFooter';
import ModalTitle from 'bloko/blocks/modal/ModalTitle';
import ModalError from 'bloko/blocks/modal/Error';

import 'bloko/blocks/modal/modal.less';

/**
 * Модальное окно, блокирующее активное содержимое страницы и переводящее его в неподвижный фон.
 * Высота и ширина модального окна регулируются его содержимым.
 */
const Modal = ({ visible, onClose, children, ...modalProps }) => {
    const [isClosable, setClosable] = useState(true);
    const [isServerENV, setServerENV] = useState(true);
    const handleClick = useCallback((event) => {
        event.stopPropagation();
        setClosable(true);
    }, []);
    const setNonClosable = useCallback(() => {
        setClosable(false);
    }, []);
    const handleClose = useCallback(
        () => {
            if (isClosable) {
                onClose();

                return;
            }

            setClosable(true);
        },
        [isClosable, onClose]
    );

    useEffect(() => {
        setServerENV(false);
    }, []);

    const handleEscPress = useCallback(
        (event) => {
            if (visible && event.keyCode === keyboard.KEY_CODES.ESC) {
                onClose();
            }
        },
        [onClose, visible]
    );

    useEffect(
        () => {
            document.addEventListener('keydown', handleEscPress);

            return () => {
                document.removeEventListener('keydown', handleEscPress);
            };
        },
        [handleEscPress]
    );

    if (isServerENV) {
        return null;
    }
    return ReactDOM.createPortal(
        <CSSTransition
            appear
            in={visible}
            timeout={150}
            mountOnEnter
            unmountOnExit
            onEntering={modalHelper.disableScroll}
            onExited={modalHelper.enableScroll}
            classNames={{
                enter: 'bloko-modal-overlay',
                enterActive: 'bloko-modal-overlay bloko-modal-overlay_visible',
                enterDone: 'bloko-modal-overlay bloko-modal-overlay_visible',
                exit: 'bloko-modal-overlay',
                exitActive: 'bloko-modal-overlay',
            }}
        >
            <div>
                <div
                    className="bloko-modal-container bloko-modal-container_visible"
                    data-qa="bloko-modal"
                    onClick={handleClose}
                >
                    <div
                        {...modalProps}
                        onMouseDown={setNonClosable}
                        onMouseUp={setNonClosable}
                        className="bloko-modal"
                        onClick={handleClick}
                        onTouchMove={handleClick}
                    >
                        {children}
                    </div>
                    <div className="bloko-modal-close-button" data-qa="bloko-modal-close" />
                </div>
            </div>
        </CSSTransition>,
        document.body
    );
};

Modal.propTypes = {
    /** Флаг открытия (рендера) модала */
    visible: PropTypes.bool,
    /** Обработчик закрытия modal */
    onClose: PropTypes.func.isRequired,
    /** Контент */
    children: PropTypes.node,
};

Modal.defaultProps = {
    visible: false,
};

export default memo(Modal);
export { ModalHeader, ModalFooter, ModalTitle, ModalError };
